/**
 * Individual symptom display card with value and optional description.
 * Shows symptom name (symptom_type from API) and value (Yes/No/N/A).
 * Description is hidden by default with "Show more" to expand.
 */

import React, { useState } from 'react';

const SymptomCard = React.memo(({ symptomType, data, className = '' }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  if (!data) return null;

  const getValueDisplay = (value, unit) => {
    if (value === 'true') return 'Yes';
    if (value === 'false') return 'No';
    if (value && value !== '') return unit ? `${value} ${unit}` : value;
    return 'N/A';
  };

  const getValueColor = (value, symptomType) => {
    const isMedicationAdherence = symptomType === 'Medication Adherence';
  
    if (isMedicationAdherence) {
      if (value === 'true') return 'bg-green-100 text-green-800'; // Yes → green
      if (value === 'false') return 'bg-red-100 text-red-800';   // No → red
    }
  
    if (value === 'true') return 'bg-red-100 text-red-800';
    if (value === 'false') return 'bg-green-100 text-green-800';
    if (value && value !== '') return 'bg-blue-100 text-blue-800';
    return 'bg-gray-100 text-gray-800';
  };

  const hasDescription = data.description && data.description.trim().length > 0;

  return (
    <div className={`bg-white p-4 rounded-lg border ${className}`}>
      <div className="flex items-center justify-between mb-2">
        <h4 className="font-semibold text-gray-800">{symptomType}</h4>
        <span className={`px-3 py-1 rounded-full text-sm font-medium ${getValueColor(data.value, symptomType)}`}>
          {getValueDisplay(data.value)}
        </span>
      </div>
      {hasDescription && (
        <div className="mt-1">
          {isExpanded ? (
            <>
              <p className="text-sm text-gray-600">{data.description}</p>
              <button
                type="button"
                onClick={() => setIsExpanded(false)}
                className="text-sm text-blue-600 hover:text-blue-800 mt-1 font-medium"
              >
                Show less
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={() => setIsExpanded(true)}
              className="text-sm text-blue-600 hover:text-blue-800 font-medium"
            >
              Show more
            </button>
          )}
        </div>
      )}
    </div>
  );
});

SymptomCard.displayName = 'SymptomCard';

export default SymptomCard;